// (C) Copyright IBM Deutschland GmbH 2021.  All rights reserved.

/***********************************************************************************************
imports
***********************************************************************************************/

import React, { useState, useEffect } from 'react';
import {Alert, Platform, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { navigationPropType } from '../propTypes';

// components
import { Picker } from '@react-native-picker/picker';
import { ListItem } from '@rneui/themed';
import VersionCheck from '../components/shared/VersionCheck';

// custom components
import { AboutListItem, AboutListLink, RedirectModal } from '../components/about';
import { Banner, Spinner, ScrollIndicatorWrapper } from '../components/shared';

// redux actions
import { reset } from '../store/sharedActions';
import { updateLanguage } from '../store/globals.slice';

// services & config
import { appConfig, theme } from '../config';
import kioskMode from '../config/kioskApiConfig';
import translate, {
  availableLanguages,
  getLanguageTag,
} from '../services/localization';
import { Routes, Stacks } from '../navigation/constants';
import {isMobile} from "react-device-detect";
import AlertModal from "../components/common/AlertModal";

/***********************************************************************************************
 * component:
 * renders the about screen which contains information about the app as well as some links to
 * websites and the screen with legal information necessary in some regions
 *
 * @param  {object}    props
 * @param  {object}    props.navigation the navigation object provided by 'react-navigation'
 **********************************************************************************************/
function SimpleAboutScreen({ navigation }) {
  const dispatch = useDispatch();

  // internal state for the redirect modal
  const [modalState, setModalState] = useState({
    hidden: true,
    modalLink: null,
  });

  // get data from global state
  const started = useSelector((state) => state.Questionnaire.started);
  const subjectId = useSelector((state) => state.User.subjectId);
  const loading = useSelector((state) => state.Globals.loading);

  useEffect(() => {
  });

  /**
   * shows a confirmation-dialog. if confirmed, it deletes the local data, logs the user
   * out and navigates back to the landing-screen.
   */
  const clearAll = () => {
    AlertModal.alert(
      translate('generic').warning,
      translate('generic').eraseAllWarning,
      [
        {
          text: translate('generic').delete,
          onPress: () => {
            dispatch(reset());
          },
        },
        {
          text: translate('generic').abort,
          style: 'cancel',
        },
      ],
      { cancelable: false },
    );
  };

  // handler for the 'change language' spinner/menu
  const changeLanguage = (languageTag) => {
    // in case some some questions of the current questionnaire have been answered,
    // warn the user that those answers will be lost when changing the language
    if (started) {
      AlertModal.alert(
        translate('generic').warning,
        translate('about').languageWarning +
          translate('about').languageWarningAddition,
        [
          {
            text: translate('generic').delete,
            onPress: () => {
              dispatch(updateLanguage({ languageTag, subjectId }));
            },
          },
          {
            text: translate('generic').abort,
            style: 'cancel',
          },
        ],
        { cancelable: false },
      );
    } else {
      dispatch(updateLanguage({ languageTag, subjectId }));
    }
  };

  let version = VersionCheck.getCurrentVersion() + ", Build " + VersionCheck.getCurrentBuildNumber();

  return (
    <View style={localStyle.wrapper}>
      {loading && <Spinner/> }
      {/* top banner */}
      <Banner
        nav={navigation}
        title={translate('about').title}
        subTitle={translate('about').subTitle}
        noMenu
      />

      {/* the modal to be opened */}
      <RedirectModal
        showModal={!modalState.hidden}
        modalLink={modalState.modalLink}
        hideModal={() => {
          setModalState({ hidden: true, modalLink: null });
        }}
      />

      {/* ScrollView with content */}
      <View style={localStyle.wrapper}>
        <ScrollIndicatorWrapper>
          <View style={localStyle.wrapper}>
            {/* holds the list-items */}
            <View style={localStyle.wrapper}>

              {/* links to the LegalInformationScreen */}
              {appConfig.allowAccessToLegalInformationScreen && (
                <ListItem
                  containerStyle={localStyle.containerStyle}
                  onPress={() => navigation.navigate(Routes.TERMS_CONDITIONS)}
                >
                  {/* title & subtitle of the listItem - the strings a identified by the webView*/}
                  <ListItem.Content>
                    <ListItem.Title style={localStyle.title}>
                      {translate('about').legal.title}
                    </ListItem.Title>

                    <ListItem.Subtitle style={localStyle.subTitle}>
                      {translate('about').legal.subTitle}
                    </ListItem.Subtitle>
                  </ListItem.Content>

                  {/* the icon on the right-hand-side */}
                  <ListItem.Chevron
                    {...{
                      type: translate('about').legal.iconType,
                      name: translate('about').legal.iconTitle,
                      color: theme.values.legalListLinkIconColor,
                      reverse: true,
                      size: 12,
                    }}
                  />
                </ListItem>
              )}




              <ListItem
                containerStyle={localStyle.containerStyle}
                onPress={
                () => navigation.navigate(Routes.TIME)
              }
            >
              {/* title & subtitle of the listItem - the strings a identified by the webView*/}
              <ListItem.Content>
                <ListItem.Title style={localStyle.title}>
                  {"Version"}
                </ListItem.Title>
                <ListItem.Subtitle style={localStyle.subTitle}>
                  {version}
                </ListItem.Subtitle>
              </ListItem.Content>
            </ListItem>
            </View>

            {/* optional buttons on the bottom of the screen - JUST FOR DEVELOPMENT*/}
            <View style={localStyle.bottom}>
              {/* language picker */}
              <View style={localStyle.languagePickerWrapper}>
                <Text style={localStyle.title}>
                  {translate('about').languageSelection}
                </Text>

                {started && (
                  <Text style={localStyle.warningSubTitle}>
                    {translate('about').languageWarning}
                  </Text>
                )}

                <Picker
                  testID="languagePicker"
                  style={localStyle.picker}
                  mode="dropdown"
                  selectedValue={getLanguageTag()}
                  onValueChange={(itemValue) => {
                    if (getLanguageTag() !== itemValue) {
                      changeLanguage(itemValue);
                    }
                  }}
                >
                  {Object.keys(availableLanguages).map((key) => (
                    <Picker.Item
                      key={key}
                      label={availableLanguages[key].title}
                      value={key}
                    />
                  ))}
                </Picker>
              </View>


            </View>
          </View>
        </ScrollIndicatorWrapper>
      </View>
    </View>
  );
}

SimpleAboutScreen.propTypes = {
  navigation: PropTypes.shape(navigationPropType).isRequired,
};

/***********************************************************************************************
local styling
***********************************************************************************************/

const localStyle = StyleSheet.create({
  wrapper: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    width: Platform.OS === 'web' && isMobile ? '100%' : 1000,
    alignSelf: 'center',
    height: '100%',
    backgroundColor: theme.colors.accent0,
  },

  bottom: {
    flex: 1,
    justifyContent: 'flex-end',
    marginBottom: 36,
    height: '100%',
    marginTop: 5,
    width: '100%',
    padding: 15,
  },

  button: {
    ...theme.classes.buttonPrimary,
    bottom: 0,
    marginTop: 10,
  },

  buttonAlert: {
    ...theme.classes.buttonAlert,
    bottom: 0,
    marginTop: 20,
  },

  buttonLabel: {
    ...theme.classes.buttonLabel,
  },

  containerStyle: {
    width: '100%',
    borderBottomColor: theme.colors.accent3,
    borderBottomWidth: 1,
    backgroundColor: theme.values.defaultListLinkBackgroundColor,
    padding: 15,
  },

  subTitle: {
    color: theme.colors.accent4,
    ...theme.fonts.body,
  },

  warningSubTitle: {
    color: theme.colors.alert,
    ...theme.fonts.body,
  },

  title: {
    ...theme.fonts.title2,
  },

  titleText: {
    width: '80%',
    textAlign: 'center',
    alignSelf: 'center',
    ...theme.fonts.header2,
  },

  languagePickerWrapper: {
    borderWidth: 3,
    borderColor: theme.colors.white,
    borderRadius: 4,
    padding: 10,
  },
});

/***********************************************************************************************
export
***********************************************************************************************/

export default SimpleAboutScreen;
